import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/api-axios';
import styles from './AdminDashboard.module.css';

const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString();
};

// User agents are long and noisy; surface the part that identifies the client.
const shortAgent = (ua) => {
    if (!ua) return '—';
    if (/Instagram/i.test(ua)) return 'Instagram in-app';
    if (/FBAN|FBAV/i.test(ua)) return 'Facebook in-app';
    if (/WhatsApp/i.test(ua)) return 'WhatsApp in-app';
    if (/Edg\//i.test(ua)) return 'Edge';
    if (/OPR\//i.test(ua)) return 'Opera';
    if (/Firefox/i.test(ua)) return 'Firefox';
    if (/Chrome/i.test(ua)) return 'Chrome';
    if (/Safari/i.test(ua)) return 'Safari';
    if (/curl|node|python/i.test(ua)) return 'Script/CLI';
    return ua.slice(0, 28);
};

const platform = (ua) => {
    if (!ua) return '';
    if (/iPhone|iPad|iOS/i.test(ua)) return 'iOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return '';
};

const AdminDashboard = () => {
    const [data, setData] = useState(null);
    const [lobbies, setLobbies] = useState(null);
    const [purging, setPurging] = useState(false);
    const [purgeResult, setPurgeResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const loadLobbies = () => {
        axios.get('/api/admin/lobbies')
            .then(res => setLobbies(res.data))
            .catch(() => setLobbies(null));
    };

    const purgeLobbies = () => {
        const count = lobbies?.purgeable ?? 0;
        if (!count) return;
        if (!window.confirm(
            `Remove ${count} lobb${count === 1 ? 'y' : 'ies'} that nobody is connected to?\n\n` +
            `Finished games are kept, and the rows are only flagged as deleted, not erased.`
        )) return;

        setPurging(true);
        axios.post('/api/admin/lobbies/purge')
            .then(res => {
                setPurgeResult(`Removed ${res.data.removed} abandoned lobb${res.data.removed === 1 ? 'y' : 'ies'}.`);
                loadLobbies();
            })
            .catch(() => setPurgeResult('Purge failed.'))
            .finally(() => setPurging(false));
    };

    const load = () => {
        loadLobbies();
        axios.get('/api/admin/visits')
            .then(res => { setData(res.data); setError(null); })
            .catch(err => {
                const status = err?.response?.status;
                setError(status === 403 || status === 401
                    ? 'This dashboard is restricted. Sign in with the admin account.'
                    : 'Could not load visitor data.');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        const timer = setInterval(load, 30000); // keep it live without hammering
        return () => clearInterval(timer);
    }, []);

    if (loading) {
        return <div className={styles.page}><div className={styles.card}><div className={styles.spinner} /></div></div>;
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.card}>
                    <div className={styles.lockIcon}>🔒</div>
                    <h2 className={styles.title}>Restricted</h2>
                    <p className={styles.muted}>{error}</p>
                    <button className={styles.button} onClick={() => navigate('/login')}>Go to sign in</button>
                </div>
            </div>
        );
    }

    const peak = Math.max(1, ...(data.daily || []).map(d => d.uniqueIps));

    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                <header className={styles.header}>
                    <div>
                        <h1 className={styles.h1}>Visitors</h1>
                        <p className={styles.muted}>Unique addresses reaching znk. Refreshes every 30s.</p>
                    </div>
                    <button className={styles.ghostButton} onClick={() => navigate('/home')}>← Back to app</button>
                </header>

                <section className={styles.statRow}>
                    <div className={styles.stat}>
                        <div className={styles.statValue}>{data.uniqueIpsAllTime}</div>
                        <div className={styles.statLabel}>Unique IPs, all time</div>
                    </div>
                    <div className={styles.stat}>
                        <div className={styles.statValue}>{data.uniqueIpsLast24h}</div>
                        <div className={styles.statLabel}>Today</div>
                    </div>
                    <div className={styles.stat}>
                        <div className={styles.statValue}>{data.uniqueIpsLast7d}</div>
                        <div className={styles.statLabel}>Last 7 days</div>
                    </div>
                    <div className={styles.stat}>
                        <div className={styles.statValue}>{data.totalHits}</div>
                        <div className={styles.statLabel}>Total requests</div>
                    </div>
                </section>

                <section className={styles.panel}>
                    <h2 className={styles.h2}>Unique visitors per day</h2>
                    {(data.daily || []).length === 0 ? (
                        <p className={styles.muted}>No visits recorded yet.</p>
                    ) : (
                        <div className={styles.chart}>
                            {data.daily.map(d => (
                                <div key={d.date} className={styles.barWrap} title={`${d.date}: ${d.uniqueIps} unique, ${d.hits} requests`}>
                                    <div className={styles.bar} style={{ height: `${Math.max(4, (d.uniqueIps / peak) * 100)}%` }} />
                                    <span className={styles.barLabel}>{d.date.slice(5)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section className={styles.panel}>
                    <div className={styles.panelHead}>
                        <h2 className={styles.h2} style={{ margin: 0 }}>
                            Lobbies
                            {lobbies && <span className={styles.tag}>{lobbies.lobbies.length} open</span>}
                            {lobbies && <span className={styles.tag}>{lobbies.onlineUsers} online</span>}
                        </h2>
                        <button
                            className={styles.dangerButton}
                            onClick={purgeLobbies}
                            disabled={purging || !lobbies || lobbies.purgeable === 0}
                            title={lobbies?.purgeable
                                ? 'Remove lobbies nobody is connected to'
                                : 'Nothing to purge — every lobby has someone connected'}
                        >
                            {purging ? 'Purging…' : `🧹 Purge empty lobbies${lobbies ? ` (${lobbies.purgeable})` : ''}`}
                        </button>
                    </div>
                    {purgeResult && <p className={styles.notice}>{purgeResult}</p>}
                    <p className={styles.muted} style={{ marginBottom: 12 }}>
                        A lobby counts as empty when nobody in it is currently connected. Players are
                        not removed when they simply close the tab, so abandoned lobbies keep showing
                        members.
                    </p>
                    <div className={styles.tableScroll}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Lobby</th><th>Host</th><th>Players</th>
                                    <th>Bots</th><th>Online</th><th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(lobbies?.lobbies || []).map(l => (
                                    <tr key={l.lobbyId}>
                                        <td>{l.name || '—'}{l.passwordProtected && <span className={styles.tag}>private</span>}</td>
                                        <td className={styles.mono}>{l.host || '—'}</td>
                                        <td>{l.participantCount}</td>
                                        <td>{l.botCount}</td>
                                        <td>{l.onlineCount}</td>
                                        <td>
                                            <span className={l.purgeable ? styles.badgeStale : styles.badgeLive}>
                                                {l.purgeable ? 'abandoned' : 'active'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {lobbies && lobbies.lobbies.length === 0 && <p className={styles.muted}>No open lobbies.</p>}
                    </div>
                </section>

                <section className={styles.panel}>
                    <h2 className={styles.h2}>Recent activity</h2>
                    <div className={styles.tableScroll}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>IP address</th>
                                    <th>Date</th>
                                    <th>Requests</th>
                                    <th>Client</th>
                                    <th>Last seen as</th>
                                    <th>Last path</th>
                                    <th>Last seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.visits || []).map((v, i) => (
                                    <tr key={`${v.ip}-${v.visitDate}-${i}`}>
                                        <td className={styles.mono}>{v.ip}</td>
                                        <td>{v.visitDate}</td>
                                        <td>{v.hits}</td>
                                        <td title={v.userAgent || ''}>
                                            {shortAgent(v.userAgent)}
                                            {platform(v.userAgent) && <span className={styles.tag}>{platform(v.userAgent)}</span>}
                                        </td>
                                        <td className={styles.mono}>{v.lastUsername || '—'}</td>
                                        <td className={styles.mono}>{v.lastPath || '—'}</td>
                                        <td>{fmt(v.lastSeen)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {(data.visits || []).length === 0 && <p className={styles.muted}>Nothing yet.</p>}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdminDashboard;
